import {
  alpha,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormGroup,
  TextField,
  Typography
} from "@mui/material"
import { Theme } from "@mui/system"
import { LOCALES } from "app/language/model"
import { translate } from "app/language/service"
import { IRootState } from "app/model"
import { FORGOT_PASSWORD_PATH, HOME_PATH, SIGNUP_PATH } from "page/model"
import { navigate } from "page/tools/navigate"
import React from "react"
import { connect } from "react-redux"
import { AppVersion } from "ui/components/AppVersion"
import { FlexBox } from "ui/components/FlexBox"
import { Logo } from "ui/components/Logo"
import { getUiConfigLoginImageSrc } from "ui/configuration/tools/common"
import { MAX_PHONE_WIDTH_IN_PX, MAX_TABLET_WIDTH_IN_PX } from "ui/model"
import { isTabletScreen } from "ui/tools/responsive"
import { EMPTY_TOKEN_VALUE, LOGIN_PAGE_ID, SIGN_UP_PAGE_ID } from "user/model"
import { getSavedRememberMe, isEmailValid, login, saveRememberMe } from "user/tools/common"
import { LocalesPopupMenu } from "./LocalesPopupMenu"
import { LoginPageShiningSquares } from "./LoginPageShiningSquares"

interface ILoginConnectProps {
  locale: LOCALES // this prop is used to trigger refresh when no user is logged in and that the language changes
  isLoggingIn: boolean
  token: string
  isTablet: boolean
}

const LoginFn = React.memo(({ isLoggingIn, isTablet, token }: ILoginConnectProps) => {
  const [errorMessage, setErrorMessage] = React.useState("")
  const [email, setEmail] = React.useState("")
  const [password, setPassword] = React.useState("")
  const [rememberMe, setRememberMe] = React.useState(getSavedRememberMe())
  const emailOK = isEmailValid(email)
  const passwordOK = typeof password === "string" && password !== ""
  const areCredentialOk = emailOK && passwordOK
  const checkErrorMessage = () => {
    if (errorMessage) {
      setErrorMessage("")
    }
  }
  const onEnter = (event: any) => {
    if (event && event.key === "Enter") {
      onLogin()
    }
  }
  const onLogin = (): void => {
    if (!email) {
      setErrorMessage(translate("login.error.email.empty"))
      return
    }
    if (!password) {
      setErrorMessage(translate("login.error.password.empty"))
      return
    }
    setErrorMessage("")
    login(email, password, rememberMe).catch((errorKey: string) => setErrorMessage(translate(errorKey)))
  }
  const onAfterLogin = () => navigate(HOME_PATH)
  React.useEffect(() => {
    if (token !== EMPTY_TOKEN_VALUE) {
      onAfterLogin()
    }
  }, [token])
  const backgroundSx: any = {}
  const imageSrc = getUiConfigLoginImageSrc()
  if (imageSrc) {
    backgroundSx.backgroundImage = `url(${imageSrc})`
    backgroundSx.backgroundRepeat = "no-repeat"
    backgroundSx.backgroundPosition = "center"
    backgroundSx.backgroundSize = "cover"
  } else {
    backgroundSx.backgroundColor = (theme: Theme) => theme.palette.primary.main
    backgroundSx.background = (theme: Theme) =>
      `linear-gradient(90deg, ${alpha(theme.palette.primary.main as string, 0.75)} 0%, ${
        theme.palette.primary.main
      } 100%)`
  }
  return (
    <FlexBox
      id={LOGIN_PAGE_ID}
      vertical
      fullWidth
      sx={{
        "position": "relative",
        "flexDirection": "column",
        "height": "100%",
        "padding": 0,
        "margin": 0,
        "boxShadow": "0 0.125rem 0.5rem 0 rgba(0, 0, 0, 0.2)",
        "backgroundColor": theme => theme.palette.background.paper,
        "& .code-version": {
          marginBottom: "1rem"
        }
      }}>
      {!imageSrc && <LoginPageShiningSquares />}
      <FlexBox
        sx={{
          ...backgroundSx,
          height: "calc(100% - 10.5rem)",
          minHeight: "calc(100% - 10.5rem)",
          maxHeight: "calc(100% - 10.5rem)",
          width: "calc(100% - 10.5rem)",
          minWidth: "calc(100% - 10.5rem)",
          maxWidth: "calc(100% - 10.5rem)",
          overflow: "hidden",
          [`@media (max-width: ${MAX_TABLET_WIDTH_IN_PX}px)`]: {
            height: "100%",
            minHeight: "100%",
            maxHeight: "100%",
            width: "100%",
            minWidth: "100%",
            maxWidth: "100%"
          }
        }}
        id={`${LOGIN_PAGE_ID}-background`}>
        <FlexBox
          gap=""
          vertical
          jc="space-evenly"
          sx={{
            "backgroundColor": theme => theme.palette.background.default,
            "borderRadius": ".375rem",
            "p": "2rem",
            "width": "25rem",
            "minWidth": "25rem",
            "maxWidth": "25rem",
            "maxHeight": "calc(100% - 2rem)",
            "pl": "3rem",
            "pr": "3rem",
            "zIndex": "1",
            "& .MuiFormControl-root": {
              borderRadius: ".375rem"
            },
            "& input": {
              px: ".4rem"
            },
            [`@media (max-width: ${MAX_PHONE_WIDTH_IN_PX}px)`]: {
              width: "20rem",
              minWidth: "20rem",
              maxWidth: "20rem"
            },
            "& .locales-menu": {
              width: "2.7rem",
              height: "2.7rem"
            }
          }}
          id={`${LOGIN_PAGE_ID}-form`}>
          <Logo id={`${LOGIN_PAGE_ID}-logo`} />
          <FlexBox gap>
            <Typography
              id={`${LOGIN_PAGE_ID}-title`}
              color="textPrimary"
              sx={{
                textTransform: "uppercase"
              }}>
              {translate("login.signin")}
            </Typography>
            <Divider orientation="vertical" sx={{ height: "1rem" }} />
            <Typography
              id={`${SIGN_UP_PAGE_ID}-title`}
              color="textSecondary"
              onClick={() => navigate(SIGNUP_PATH)}
              sx={{
                "textTransform": "uppercase",
                "&:hover": {
                  cursor: "pointer",
                  color: theme => theme.palette.primary.main
                }
              }}>
              {translate("login.signup")}
            </Typography>
          </FlexBox>
          <TextField
            id={`${LOGIN_PAGE_ID}-email`}
            sx={{
              width: "100%",
              mt: "1rem",
              mb: "1rem"
            }}
            label={translate("login.placeholder.email")}
            variant="outlined"
            required
            type="email"
            value={email}
            onChange={(evt: React.ChangeEvent<HTMLInputElement>): void => {
              checkErrorMessage()
              setEmail(evt.target.value)
            }}
            onKeyUp={onEnter}
            disabled={isLoggingIn}
          />
          <TextField
            id={`${LOGIN_PAGE_ID}-password`}
            sx={{
              width: "100%"
            }}
            variant="outlined"
            label={translate("login.title.password")}
            required
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(evt: React.ChangeEvent<HTMLInputElement>): void => {
              checkErrorMessage()
              setPassword(evt.target.value)
            }}
            onKeyUp={onEnter}
            disabled={isLoggingIn}
          />
          {errorMessage && (
            <Typography
              id={`${LOGIN_PAGE_ID}-error`}
              className="main-panel-login-error"
              fontSize=".9rem"
              color="error"
              align="center"
              sx={{ width: "100%", mt: "1rem", mb: ".2rem" }}>
              {errorMessage}
            </Typography>
          )}
          <FormGroup
            sx={{
              width: "100%"
            }}>
            <FormControlLabel
              id={`${LOGIN_PAGE_ID}-remember`}
              sx={{
                "& span": { fontSize: ".85rem" },
                "& .MuiFormControlLabel-label": {
                  color: theme => theme.palette.text.primary
                }
              }}
              control={
                <Checkbox
                  value={rememberMe}
                  checked={rememberMe}
                  size="small"
                  onChange={(_event, checked) => {
                    saveRememberMe(checked)
                    setRememberMe(checked)
                  }}
                />
              }
              label={translate("label.remember.me")}
            />
          </FormGroup>
          <Button
            sx={{
              width: "10rem",
              height: "3rem",
              mt: "1rem",
              textTransform: "uppercase",
              fontWeight: "bold"
            }}
            disabled={!areCredentialOk || isLoggingIn}
            id={`${LOGIN_PAGE_ID}-button`}
            color="primary"
            startIcon={isLoggingIn ? <CircularProgress size="1.5rem" /> : undefined}
            variant="contained"
            onClick={(): void => onLogin()}>
            {translate("login.button.connect")}
          </Button>
          <Divider sx={{ width: "100%", mt: "1rem", mb: "1rem" }} />
          <FlexBox gap fullWidth jc="space-between">
            <LocalesPopupMenu />
            <Typography
              id={`${LOGIN_PAGE_ID}-forgot-password`}
              color="textSecondary"
              onClick={() => navigate(FORGOT_PASSWORD_PATH)}
              fontSize=".85rem"
              sx={{ "&:hover": { color: theme => theme.palette.primary.main, cursor: "pointer" } }}>
              {translate("user.password.forgotten")}?
            </Typography>
          </FlexBox>
          <AppVersion
            sx={{
              color: (theme: any) => (isTablet ? theme.palette.text.primary : theme.palette.text.secondary),
              position: "fixed",
              bottom: "1rem"
            }}
          />
        </FlexBox>
      </FlexBox>
    </FlexBox>
  )
})

export const LoginPage = connect(
  (state: IRootState): ILoginConnectProps => ({
    locale: state.language.locale,
    isLoggingIn: state.user.isLoggingIn,
    token: state.user.token,
    isTablet: isTabletScreen(state)
  })
)(LoginFn)
